
import React, { useState, useEffect } from 'react';
import { ArrowRight, CheckCircle2, ShieldCheck, Clock, Zap, UserCheck, Quote, AlertTriangle, Calendar, Heart, Brain, Search, Check, HelpCircle } from 'lucide-react';
import logo2 from '../assets/logo2.png';
import ricardo from '../assets/ricardo.png';
import ana from '../assets/ana.png';
import lucas from '../assets/lucas.png';

const LandingPageV3 = ({ onGetStarted }) => {
    useEffect(() => {
        document.documentElement.classList.remove('dark');
        return () => {
            const savedTheme = localStorage.getItem('theme');
            if (savedTheme === 'dark') {
                document.documentElement.classList.add('dark');
            }
        };
    }, []);

    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const scrollToPricing = () => {
        const pricingSection = document.getElementById('pricing');
        if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleSubscribe = async (priceId) => {
        try {
            const apiBaseUrl = process.env.REACT_APP_API_URL || '';
            const response = await fetch(`${apiBaseUrl}/api/create-public-checkout-session`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    price_id: priceId,
                    cancel_url: window.location.href
                })
            });

            const { url } = await response.json();
            if (url) window.location.href = url;
            else alert('Erro ao iniciar checkout. Tente novamente.');
        } catch (err) {
            console.error("Payment error", err);
            alert("Erro ao conectar com o servidor.");
        }
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans selection:bg-primary/20">

            {/* Hero Section */}
            <section className="relative pt-24 pb-36 px-4 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-background to-background -z-10" />
                <div className="max-w-4xl mx-auto text-center space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-accent text-muted-foreground text-sm font-medium animate-fade-in-up">
                        <span>Para quem sempre diz "depois"</span>
                    </div>

                    <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight">
                        Bem-vindo ao clube dos<br />
                        <span className="text-primary">"Vou fazer isso amanhã"</span>
                    </h1>

                    <h2 className="text-xl md:text-2xl font-bold text-muted-foreground">
                        (Spoiler: amanhã nunca chega)
                    </h2>

                    <div className="max-w-2xl mx-auto mt-8 text-lg text-muted-foreground leading-relaxed">
                        <p className="mb-4">Oi. Senta aí que eu preciso te contar uma coisa.</p>
                        <p>Você conhece aquele curso que você comprou em 2022 cheio de empolgação? Pois é. Ele ainda tá lá, te olhando com cara de decepção.</p>
                        <p className="font-bold text-foreground mt-4">Relaxa. A gente não tá aqui pra te julgar.</p>
                        <p>A gente TÁ aqui pra te ajudar a finalmente fazer aquela bagaça.</p>
                    </div>

                    <div className="pt-10">
                        <button
                            onClick={scrollToPricing}
                            className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-bold text-primary-foreground bg-primary rounded-full hover:bg-primary/90 transition-all duration-300 shadow-xl hover:shadow-primary/25 hover:-translate-y-1"
                        >
                            CHEGA DE "DEPOIS" — COMEÇAR AGORA
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                        <p className="mt-4 text-sm text-center text-muted-foreground">Experimente por 30 dias sem risco</p>
                    </div>
                </div>
            </section>

            {/* The Quiz */}
            <section className="py-20 bg-muted/30 border-y border-border/50">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="text-2xl md:text-3xl font-bold text-center mb-10">O teste definitivo: Você é procrastinador raiz?</h2>

                    <div className="bg-background rounded-2xl p-6 md:p-8 shadow-sm border border-border space-y-4">
                        {[
                            "Você tem pelo menos 3 cursos online que assistiu só a aula de boas-vindas",
                            "Já baixou um app de produtividade e esqueceu que ele existe no dia seguinte",
                            "Tem uma planilha linda de planejamento que usou exatamente 1 vez",
                            "Conhece a frase \"tá na minha lista\" melhor que o próprio nome",
                            "Já limpou a casa inteira só pra não fazer aquela tarefa importante",
                            "Reorganizou a estante, fez café, cortou as unhas... tudo menos O QUE PRECISAVA FAZER",
                            "Tem aquele projeto que \"vai começar na segunda\" há 8 segundas-feiras seguidas"
                        ].map((item, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <div className="mt-1 w-5 h-5 rounded border-2 border-primary/50 flex items-center justify-center shrink-0">
                                    <div className="w-3 h-3 bg-primary rounded-sm opacity-50"></div>
                                </div>
                                <span className="text-lg text-muted-foreground">{item}</span>
                            </div>
                        ))}
                    </div>

                    <div className="mt-8 text-center bg-primary/10 rounded-xl p-6 border border-primary/20">
                        <p className="font-bold text-primary text-lg">Se você marcou 3 ou mais: Parabéns! Você é oficialmente um de nós.</p>
                        <p className="text-sm text-muted-foreground mt-1">Bem-vindo ao maior clube do Brasil (infelizmente).</p>
                    </div>
                </div>
            </section>

            {/* The Saga (Pain) */}
            <section className="py-24">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-center mb-16">A saga do procrastinador brasileiro</h2>

                    <div className="relative border-l-2 border-dashed border-border ml-4 md:ml-1/2 space-y-10">
                        {[
                            { time: "🌅 7h00", title: "O Otimista Iludido", text: '"HOJE VAI SER DIFERENTE. Hoje eu vou arrasar."' },
                            { time: "☕ 7h30", title: "O Café da Procrastinação", text: '"Deixa eu só tomar um café bem tomado antes."' },
                            { time: "📱 8h15", title: "A Armadilha Digital", text: '"Vou só dar uma olhadinha no WhatsApp."' },
                            { time: "😰 12h30", title: "A Ansiedade Bate", text: 'Ainda no celular. Aquela sensação ruim subindo.' },
                            { time: "🍕 13h00", title: "A Fuga Gastronômica", text: '"Vou almoçar primeiro, depois eu começo com tudo."' },
                            { time: "📺 16h00", title: "A Mentira do Relaxamento", text: '"Só um episódio pra relaxar antes de começar."' },
                            { time: "😱 18h00", title: "O Pânico", text: '"CARACA, JÁ É 18H?! Amanhã eu começo mais cedo, prometo."' },
                            { time: "😭 23h00", title: "A Culpa Noturna", text: 'Deitado na cama, culpa corroendo, prometendo que amanhã será diferente.' },
                            { time: "🔄 Terça", title: "O Loop", text: 'Repeat.', highlight: true }
                        ].map((item, i) => (
                            <div key={i} className="relative pl-8 md:pl-0">
                                <div className="md:absolute md:left-1/2 md:-translate-x-[9px] -left-[9px] absolute top-0 w-4 h-4 rounded-full bg-background border-4 border-primary z-10"></div>
                                <div className={`md:w-1/2 ${i % 2 === 0 ? 'md:mr-auto md:pr-12 md:text-right' : 'md:ml-auto md:pl-12'} relative`}>
                                    <span className="text-sm font-bold text-primary mb-1 block">{item.time}</span>
                                    <div className={`p-5 rounded-2xl border ${item.highlight ? 'border-destructive bg-destructive/5' : 'border-border bg-muted/20'}`}>
                                        <h3 className="font-bold text-lg mb-1">{item.title}</h3>
                                        <p className="text-muted-foreground italic">"{item.text}"</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center max-w-2xl mx-auto">
                        <p className="text-xl font-bold">Ficou desconfortável? <br /><span className="text-destructive">É porque você TÁ NESSA FOTO e você sabe disso.</span></p>
                    </div>
                </div>
            </section>

            {/* The Explanation */}
            <section className="py-20 bg-muted/30 text-center">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="text-3xl font-bold mb-8">Por que a gente adia tudo?</h2>
                    <p className="text-lg text-muted-foreground mb-6">A explicação científica que pelo menos te faz rir da tua dor:</p>

                    <div className="bg-background p-8 rounded-3xl shadow-sm border border-border inline-block text-left mb-8">
                        <p className="text-xl font-medium mb-4">🧠 Seu cérebro é basicamente um <strong>golden retriever bêbado</strong>.</p>
                        <p className="mb-4">Ele vê a bolinha (a tarefa importante) mas no meio do caminho:</p>
                        <ul className="space-y-2 mb-6 text-muted-foreground">
                            <li>• "OPA, TEM UMA NOTIFICAÇÃO!"</li>
                            <li>• "OLHA, UM MEME!"</li>
                            <li>• "SERÁ QUE AQUELE EX JÁ CASOU?"</li>
                            <li>• "PRECISO DESCOBRIR SE ÁGUA MOLHADA É REDUNDÂNCIA"</li>
                        </ul>
                        <div className="bg-primary/10 p-4 rounded-xl text-center">
                            <p className="text-sm font-bold text-primary">3 horas depois, você nem lembra qual era a tarefa original.</p>
                        </div>
                    </div>

                    <p className="text-lg">Não é culpa sua. Seu cérebro foi programado pra evitar esforço.<br /><strong>E aí, meu amigo, você precisa de um truque.</strong></p>
                </div>
            </section>

            {/* Introducing Solution */}
            <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
                    <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 inline-block">A Solução</span>
                    <h2 className="text-4xl md:text-5xl font-black mb-6">SkipLater</h2>
                    <p className="text-2xl font-medium opacity-90 mb-10">(ou "Para de Inventar Desculpa, Vai")</p>

                    <div className="grid md:grid-cols-2 gap-8 text-left">
                        <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/20">
                            <h3 className="font-bold text-xl mb-2 text-white">O que NÃO TEMOS:</h3>
                            <ul className="space-y-2 opacity-80 text-sm">
                                <li>❌ Papo de "guerreiro"</li>
                                <li>❌ "O sucesso não dorme"</li>
                                <li>❌ Acordar às 5h da manhã</li>
                            </ul>
                        </div>
                        <div className="bg-white text-primary p-6 rounded-2xl shadow-xl border-2 border-white">
                            <h3 className="font-bold text-xl mb-2">O que TEMOS:</h3>
                            <ul className="space-y-2 text-primary/80 text-sm font-medium">
                                <li>✅ Um sistema que funciona sem vontade</li>
                                <li>✅ Micro-passos ridículos de fáceis</li>
                                <li>✅ Resultados reais (finalmente)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="py-24">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-center mb-16">Como funciona (sem mimimi)</h2>
                    <div className="space-y-6">
                        {[
                            { step: 1, title: "Você confessa o que te assombra", desc: '"Preciso terminar o TCC", "Tenho que estudar"...' },
                            { step: 2, title: "A gente quebra em pedaços RIDÍCULOS", desc: 'Não é "fazer o relatório". É "abrir o documento". Só.' },
                            { step: 3, title: "Você faz o micro-passo AGORA", desc: 'Leva 30 segundos. Seu cérebro nem percebe que tá trabalhando.' },
                            { step: 4, title: "A magia acontece", desc: 'Começar é difícil. Continuar é fácil. Depois que abre, você vai.' },
                            { step: 5, title: "Você vira um unicórnio 🦄", desc: 'Lenda urbana: A Pessoa Que Realmente Faz as Coisas.' }
                        ].map((s, i) => (
                            <div key={i} className="flex gap-6 items-start p-6 rounded-2xl border border-border hover:border-primary/50 transition-colors bg-muted/10">
                                <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">{s.step}</div>
                                <div>
                                    <h3 className="text-xl font-bold mb-2">{s.title}</h3>
                                    <p className="text-muted-foreground">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-20 bg-muted/30">
                <div className="container mx-auto px-4 max-w-4xl">
                    <h2 className="text-3xl font-bold text-center mb-12">O que você ganha</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            { label: "Paz de espírito", desc: "Dormir sem aquela lista mental te atormentando" },
                            { label: "Orgulho próprio", desc: 'Olhar no espelho e pensar "eu sou foda"' },
                            { label: "Tempo livre DE VERDADE", desc: "Aquele Netflix sem culpa, sabe?" },
                            { label: "Respeito dos outros", desc: '"Como você faz tanta coisa?" (você só começou, ué)' },
                            { label: "Fim da auto-sabotagem", desc: "Chega de prometer e não cumprir pra si mesmo" },
                            { label: "Menos ansiedade", desc: "Fazer é menos estressante que adiar" }
                        ].map((b, i) => (
                            <div key={i} className="bg-background p-6 rounded-xl shadow-sm border border-border flex flex-col justify-center">
                                <div className="flex items-center gap-2 mb-2">
                                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    <span className="font-bold text-lg">{b.label}</span>
                                </div>
                                <p className="text-muted-foreground text-sm pl-7">{b.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 bg-background">
                <div className="container mx-auto px-4 max-w-6xl">
                    <h2 className="text-3xl font-bold text-center mb-16">Depoimentos de ex-procrastinadores</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            { q: "Eu tinha um TCC parado há 7 MESES. Com o SkipLater terminei em 3 semanas. Minha orientadora quase infartou.", a: "Thiago, 26, quase não formou", img: lucas },
                            { q: "Achei que era preguiçosa. Descobri que só precisava de alguém dizendo 'faz só isso agora'. Funciona tipo mágica.", a: "Amanda, 32, ex-procrastinadora", img: ana },
                            { q: "Comecei 5 negócios, terminei zero. Aí veio o SkipLater e lancei meu projeto. Tá faturando e tudo. Bizarro.", a: "Rafael, 29, empreendedor", img: ricardo }
                        ].map((t, i) => (
                            <div key={i} className="bg-muted/30 p-8 rounded-3xl relative flex flex-col">
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary/20 shrink-0">
                                        <img src={t.img} alt={t.a.split(',')[0]} className="w-full h-full object-cover" />
                                    </div>
                                    <p className="font-bold text-foreground text-sm">{t.a}</p>
                                </div>
                                <Quote className="w-8 h-8 text-primary/20 mb-4 absolute top-8 right-8" />
                                <p className="text-lg italic text-muted-foreground">"{t.q}"</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Common Objections */}
            <section className="py-20 border-t border-border/50">
                <div className="container mx-auto px-4 max-w-3xl text-center">
                    <h2 className="text-3xl font-bold mb-12">Perguntas que você tá fazendo agora</h2>

                    <div className="space-y-8 text-left">
                        <div className="group">
                            <h3 className="font-bold text-xl mb-2 group-hover:text-primary transition-colors">"Mas eu já tentei de tudo..."</h3>
                            <p className="text-muted-foreground">Você tentou tudo EXCETO um sistema que trabalha A FAVOR do seu cérebro de golden retriever. Confia.</p>
                        </div>
                        <div className="group">
                            <h3 className="font-bold text-xl mb-2 group-hover:text-primary transition-colors">"E se eu não usar?"</h3>
                            <p className="text-muted-foreground">A gente devolve seu dinheiro em 30 dias se você não gostar. Então você perde literalmente nada tentando.</p>
                        </div>
                        <div className="group">
                            <h3 className="font-bold text-xl mb-2 group-hover:text-primary transition-colors">"Precisa instalar app, fazer cadastro?"</h3>
                            <p className="text-muted-foreground">Precisa. Mas leva 2 minutos. Você consegue. A gente acredita em você.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing & Final CTA */}
            <section id="pricing" className="py-24 px-4 bg-muted/30 relative">
                <div className="container px-4 mx-auto max-w-5xl">

                    <div className="text-center max-w-3xl mx-auto mb-16">
                        <h2 className="text-4xl font-bold mb-6">Olha, vou ser sincero contigo</h2>
                        <p className="text-xl text-muted-foreground">
                            Você pode fechar essa página e continuar com aquele projeto na cabeça, se sentindo culpado.<br />
                            <strong>OU... Você pode dar uma chance pra si mesmo agora.</strong>
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto items-center">
                        {/* Monthly Plan */}
                        <div className="bg-background rounded-3xl p-8 border border-border flex flex-col items-center text-center opacity-80 hover:opacity-100 transition-opacity">
                            <h3 className="text-xl font-bold mb-2">Plano Mensal</h3>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">R$ 9,90</span>
                                <span className="text-muted-foreground text-sm font-medium">/mês</span>
                            </div>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_MONTHLY)}
                                className="w-full py-4 rounded-xl border border-input font-bold hover:bg-accent hover:text-accent-foreground transition-all"
                            >
                                Assinar Mensal
                            </button>
                            <p className="text-xs text-muted-foreground mt-4">Preço de um lanche.</p>
                        </div>

                        {/* Yearly Plan */}
                        <div className="bg-background rounded-3xl p-10 border-2 border-primary shadow-2xl flex flex-col items-center text-center transform md:scale-105 relative">
                            <div className="absolute -top-4 bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wider">Recomendado</div>
                            <h3 className="text-2xl font-bold mb-2">Plano Anual</h3>
                            <div className="mb-6">
                                <span className="text-5xl font-bold">R$ 97</span>
                                <span className="text-muted-foreground text-sm font-medium">/ano</span>
                            </div>
                            <p className="text-sm text-green-600 font-bold mb-6">Economize 20%</p>
                            <button
                                onClick={() => handleSubscribe(process.env.REACT_APP_STRIPE_PRICE_YEARLY)}
                                className="w-full py-5 rounded-xl bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/25 hover:-translate-y-1"
                            >
                                TÁ BOM, EU VOU PARAR DE ENROLAR
                            </button>
                            <p className="text-xs text-muted-foreground mt-4">30 dias pra pedir o dinheiro de volta.</p>
                        </div>
                    </div>

                    <div className="mt-20 text-center max-w-2xl mx-auto">
                        <div className="text-sm bg-background border border-border p-6 rounded-xl inline-block shadow-sm">
                            <p className="font-bold mb-2 flex items-center justify-center gap-2"><ShieldCheck className="w-5 h-5 text-primary" /> Garantia Anti-Frustração</p>
                            <p className="text-muted-foreground">Usa por 30 dias. Se não sentir que tá fazendo as coisas, a gente devolve 100%. Sem drama.</p>
                        </div>
                    </div>

                    <div className="mt-12 text-center text-sm text-muted-foreground space-y-2">
                        <p><strong>P.S.:</strong> Você leu até aqui. Isso é um sinal. Bora.</p>
                        <p><strong>P.P.S.:</strong> "Depois" não existe. O único momento é esse.</p>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-background border-t border-border/50">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="text-3xl font-bold text-center mb-12">Perguntas Frequentes</h2>
                    <div className="space-y-4">
                        {[
                            { q: "Quanto tempo por dia preciso dedicar?", a: "5 minutos no começo. Sério. Menos tempo que você gasta rolando feed." },
                            { q: "Funciona pra qualquer tipo de tarefa?", a: "Desde academia até escrever livro. Se dá pra adiar, dá pra usar o SkipLater." },
                            { q: "E se eu esquecer de usar?", a: "A gente te lembra. Mas não de forma chata. De forma tipo 'e aí, bora fazer aquilo?'" },
                            { q: "Posso usar no celular?", a: "Pode. E no computador. E no tablet. E aonde você mais procrastina." },
                            { q: "Tem suporte em português?", a: "100% BR, parceiro. A gente até entende gíria." }
                        ].map((faq, index) => (
                            <div key={index} className="border border-border rounded-lg overflow-hidden">
                                <button
                                    className="w-full flex items-center justify-between p-4 text-left font-medium hover:bg-accent/50 transition-colors"
                                    onClick={() => toggleFaq(index)}
                                >
                                    {faq.q}
                                    {openFaqIndex === index ? (
                                        <HelpCircle className="w-5 h-5 text-primary rotate-180 transition-transform" />
                                    ) : (
                                        <HelpCircle className="w-5 h-5 text-muted-foreground transition-transform" />
                                    )}
                                </button>
                                {openFaqIndex === index && (
                                    <div className="p-4 pt-0 text-muted-foreground bg-accent/20">
                                        <p>{faq.a}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 bg-background border-t border-border/50">
                <div className="container px-4 mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="flex items-center gap-2">
                        <img src={logo2} alt="SkipLater" className="h-8 w-auto rounded-lg grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all" />
                        <span className="text-sm text-muted-foreground font-medium">SkipLater © 2025</span>
                    </div>

                    <div className="flex gap-6 text-sm text-muted-foreground">
                        <a href="/privacy" className="hover:text-foreground transition-colors">Politica de Privacidade</a>
                        <a href="/terms" className="hover:text-foreground transition-colors">Termos de Uso</a>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPageV3;
